import React, { useState, useRef, useEffect } from 'react';
import './FileUploader.css';

/**
 * FileUploader - Componente minimalista para subir archivos
 * Detecta automáticamente: Excel (.xlsx, .xls), PDF (.pdf), Imágenes (.jpg, .png, .gif, .webp)
 */
const FileUploader = ({ onFileUpload, disabled = false, clearTrigger = null }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // Limpiar cuando el componente padre lo solicita mediante clearTrigger
  useEffect(() => {
    if (clearTrigger !== null && uploadedFile) {
      handleClearFile();
    }
  }, [clearTrigger]);

  // Tipos de archivo soportados
  const SUPPORTED_FILES = {
    excel: {
      extensions: ['.xlsx', '.xls', '.xlsm'],
      mimeTypes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'],
      icon: '📊',
      label: 'Excel'
    },
    pdf: {
      extensions: ['.pdf'],
      mimeTypes: ['application/pdf'],
      icon: '📄',
      label: 'PDF'
    },
    image: {
      extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      icon: '🖼️',
      label: 'Imagen'
    }
  };

  // Detectar tipo de archivo
  const detectFileType = (file) => {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    for (const [type, config] of Object.entries(SUPPORTED_FILES)) {
      const hasValidExtension = config.extensions.some(ext => fileName.endsWith(ext));
      const hasValidMimeType = config.mimeTypes.some(mime => mimeType.includes(mime));

      if (hasValidExtension || hasValidMimeType) {
        return type;
      }
    }

    return null;
  };

  // Manejar selección de archivo
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsProcessing(true);

    try {
      const type = detectFileType(file);

      if (!type) {
        alert(`Tipo de archivo no soportado. Formatos válidos:\n• Excel: ${SUPPORTED_FILES.excel.extensions.join(', ')}\n• PDF: ${SUPPORTED_FILES.pdf.extensions.join(', ')}\n• Imágenes: ${SUPPORTED_FILES.image.extensions.join(', ')}`);
        setIsProcessing(false);
        return;
      }

      setFileType(type);
      console.log(`[FileUploader] 📁 Tipo detectado: ${type} | Archivo: ${file.name}`);

      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const base64String = event.target.result.split(',')[1];

          let previewData = null;
          if (type === 'image') {
            previewData = event.target.result;
          } else if (type === 'pdf') {
            previewData = {
              pages: '...',
              size: `${(file.size / 1024).toFixed(2)} KB`
            };
          } else if (type === 'excel') {
            previewData = {
              sheets: '...',
              size: `${(file.size / 1024).toFixed(2)} KB`
            };
          }

          setPreview(previewData);

          const fileData = {
            type,
            filename: file.name,
            mimeType: file.type,
            base64: base64String,
            size: file.size
          };

          setUploadedFile(fileData);

          if (onFileUpload) {
            onFileUpload(fileData);
          }

          console.log(`[FileUploader] ✅ Archivo listo: ${file.name}`);

        } catch (error) {
          console.error('[FileUploader] ❌ Error procesando archivo:', error);
          alert('Error al procesar el archivo. Por favor intenta de nuevo.');
        } finally {
          setIsProcessing(false);
        }
      };

      reader.onerror = () => {
        console.error('[FileUploader] ❌ Error leyendo archivo');
        alert('Error al leer el archivo');
        setIsProcessing(false);
      };

      reader.readAsDataURL(file);

    } catch (error) {
      console.error('[FileUploader] ❌ Error:', error);
      alert('Error al procesar el archivo');
      setIsProcessing(false);
    }
  };

  // Limpiar archivo subido
  const handleClearFile = () => {
    setUploadedFile(null);
    setPreview(null);
    setFileType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onFileUpload) {
      onFileUpload(null);
    }
    console.log('[FileUploader] 🗑️ Archivo eliminado');
  };

  // Abrir selector de archivos
  const handleClick = () => {
    if (disabled || isProcessing) return;
    fileInputRef.current?.click();
  };

  return (
    <div className="file-uploader-container">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.xlsm,.pdf,.jpg,.jpeg,.png,.gif,.webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled || isProcessing}
      />

      {!uploadedFile ? (
        <button
          className="upload-button"
          onClick={handleClick}
          disabled={disabled || isProcessing}
          title="Adjuntar archivo (Excel, PDF o Imagen)"
        >
          {isProcessing ? (
            <>
              <span className="spinner"></span>
            </>
          ) : (
            <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          )}
        </button>
      ) : (
        <div
          className="file-preview-card"
          data-file-type={fileType}
        >
          <div className="file-preview-header">
            <div className="file-info">
              <span className="file-type-icon">{SUPPORTED_FILES[fileType].icon}</span>
              <div className="file-details">
                <span className="file-name" title={uploadedFile.filename}>
                  {uploadedFile.filename}
                </span>
                <span className="file-type-label">
                  {SUPPORTED_FILES[fileType].label} • {(uploadedFile.size / 1024).toFixed(1)} KB
                </span>
              </div>
            </div>
            <button
              className="clear-file-button"
              onClick={handleClearFile}
              title="Eliminar archivo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
